import React from "react";
import { Box, chakra, Flex, Icon, Text, useCheckbox, useRadio, UseCheckboxProps, UseRadioProps } from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import { motion, useReducedMotion } from "framer-motion";
import { alpha, useGovTheme } from "./useGovTheme";

/**
 * A ballot choice as a big card you can hit with a thumb: the choice's words in large type, a round
 * (pick one) or square (tick any) box on the right that fills and pops a tick when chosen. The real
 * radio/checkbox input is inside, visually hidden, so keyboards, screen readers and forms behave exactly
 * as they did with the plain controls. The tick is instant for "reduce motion". Added 2026-09-12.
 *
 * Tooltip rule (lib/blurClick.ts): a pick made with a mouse or finger blurs the input afterwards, so a
 * wrapping GovTip does not stick open. A pick made with the keyboard keeps focus — arrow keys have to go on
 * moving between the choices.
 */
function useBlurAfterPointerPick() {
  const pointer = React.useRef(false);
  return {
    labelHandlers: {
      onPointerDown: () => { pointer.current = true; },
      onClick: () => { window.setTimeout(() => { pointer.current = false; }, 0); },
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (pointer.current) { pointer.current = false; e.currentTarget.blur(); }
    },
  };
}
function ChoiceFace({ checked, focused, disabled, round, label, hint }: {
  checked: boolean; focused: boolean; disabled: boolean; round: boolean; label: string; hint?: string;
}) {
  const t = useGovTheme();
  const reduce = useReducedMotion();
  return (
    <Flex align="center" gap={4} minH="64px" px={{ base: 4, md: 5 }} py={3} borderRadius="xl" borderWidth="2px" w="100%"
      borderColor={checked ? t.primary : t.borderStrong}
      bg={checked ? alpha(t.primary, 0.08) : t.surface}
      boxShadow={focused ? `0 0 0 3px ${alpha(t.primary, 0.45)}` : checked ? `0 0 0 4px ${alpha(t.primary, 0.14)}` : "none"}
      cursor={disabled ? "not-allowed" : "pointer"} opacity={disabled ? 0.6 : 1}
      transition="border-color .15s ease, background .15s ease, box-shadow .15s ease"
      _hover={disabled ? undefined : { borderColor: t.primary }}>
      <Box flex={1} minW={0}>
        <Text fontSize="lg" fontWeight={checked ? "bold" : "semibold"} color={t.text} wordBreak="break-word">{label}</Text>
        {hint && <Text fontSize="sm" color={t.muted}>{hint}</Text>}
      </Box>
      <Flex w={8} h={8} flexShrink={0} borderRadius={round ? "full" : "lg"} borderWidth="2px" align="center" justify="center"
        borderColor={checked ? t.primary : t.borderStrong} bg={checked ? t.primary : "transparent"} transition="background .15s ease, border-color .15s ease">
        {/* Always mounted and animated between two states — no AnimatePresence, whose framer-motion 11
            typing (a component returning `Element | undefined`) does not compile on this repo's TypeScript 4.9. */}
        <motion.span aria-hidden style={{ display: "flex" }} initial={false}
          animate={checked ? { scale: 1, rotate: 0, opacity: 1 } : { scale: 0, rotate: -35, opacity: 0 }}
          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 560, damping: 22 }}>
          <Icon as={FiCheck} color="white" boxSize={5} />
        </motion.span>
      </Flex>
    </Flex>
  );
}

/** One choice inside a Chakra `useRadioGroup` — pass the group's `getRadioProps({ value })`. */
export function GovRadioCard({ label, hint, ...radio }: UseRadioProps & { label: string; hint?: string }) {
  const { state, getInputProps, getRadioProps, htmlProps } = useRadio(radio);
  const blur = useBlurAfterPointerPick();
  return (
    <chakra.label {...htmlProps} {...blur.labelHandlers} display="block" w="100%">
      <input {...getInputProps({ onChange: blur.onChange })} />
      <Box {...getRadioProps()}>
        <ChoiceFace checked={state.isChecked} focused={state.isFocused} disabled={Boolean(state.isDisabled)} round label={label} hint={hint} />
      </Box>
    </chakra.label>
  );
}

/** One choice inside a Chakra `useCheckboxGroup` — pass the group's `getCheckboxProps({ value })`. */
export function GovCheckCard({ label, hint, ...checkbox }: UseCheckboxProps & { label: string; hint?: string }) {
  const { state, getInputProps, getCheckboxProps, htmlProps } = useCheckbox(checkbox);
  const blur = useBlurAfterPointerPick();
  return (
    <chakra.label {...htmlProps} {...blur.labelHandlers} display="block" w="100%">
      <input {...getInputProps({ onChange: blur.onChange })} />
      <Box {...getCheckboxProps()}>
        <ChoiceFace checked={state.isChecked} focused={state.isFocused} disabled={Boolean(state.isDisabled)} round={false} label={label} hint={hint} />
      </Box>
    </chakra.label>
  );
}
